import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ActionGetOrderByID } from '../../../store/Actions/OrderHistoryAction';
import { ActionUpdatePatient, CreatePatientConfirmationDetail, ActionGetCCPaperDetail } from '../../../store/Actions/PatientAction';
import { ActionCheckIn } from '../../../store/Actions/MasterDataAction';

// Hook for fetching check-in data
export const useCheckInData = (orderId) => {
    const dispatch = useDispatch();
    const patientData = useSelector(state => state?.patient?.CcPaperDetail);
    const orderDetail = useSelector(state => state?.OrderHistory?.orderDetail);
    const roomSlot = useSelector(state => state?.MasterData?.roomSlot);

    useEffect(() => {
        if (orderId) {
            dispatch(ActionGetOrderByID({ Query: `/${orderId}` }));
        }
    }, [orderId, dispatch]);

    return { patientData, orderDetail, roomSlot };
};

// Hook for Aadhaar validation
export const useAadhaarValidation = () => {
    const [aadhaarError, setAadhaarError] = useState('');

    const validateAadhaar = useCallback((aadhaarNumber) => {
        if (!aadhaarNumber) {
            return 'Aadhaar number is required';
        }
        if (!/^\d{12}$/.test(aadhaarNumber)) {
            return 'Aadhaar number must be exactly 12 digits';
        }
        return '';
    }, []);

    const validateEmergencyNumber = useCallback((emergencyNumber) => {
        if (!emergencyNumber) {
            return 'Emergency contact number is required';
        }
        if (!/^\d{10}$/.test(emergencyNumber)) {
            return 'Emergency contact number must be exactly 10 digits';
        }
        return '';
    }, []);

    const validateAllPersons = useCallback((persons) => {
        const errors = [];
        persons.forEach((person) => {
            const aadhaarError = validateAadhaar(person.aadhaarCardNumber);
            if (aadhaarError) {
                errors.push(`${person.name}: ${aadhaarError}`);
            }

            const emergencyError = validateEmergencyNumber(person.emergencyNumber);
            if (emergencyError) {
                errors.push(`${person.name}: ${emergencyError}`);
            }
        });
        return errors;
    }, [validateAadhaar, validateEmergencyNumber]);

    const validateArrivalInfo = useCallback((arrivingFrom, arrivalTime) => {
        if (!arrivingFrom) return 'Arriving from is required';
        if (!arrivalTime) return 'Arrival time is required';
        return '';
    }, []);

    return { aadhaarError, setAadhaarError, validateAadhaar, validateEmergencyNumber, validateAllPersons, validateArrivalInfo };
};

// Helper function to build person object from case paper data
const buildPersonFromCasePaper = (casePaperData, personInfo, personType, guestIndex = 0) => {
    const confirmation = casePaperData?.confirmationDetails;
    const aadhaarIndex = personType === 'main' ? 0 : guestIndex + 1;


    return {
        id: casePaperData?._id || personInfo?.id || '',
        name: casePaperData?.name || personInfo?.name || '-',
        type: personType,
        aadhaarCardNumber: casePaperData?.aadhaarCardNumber || confirmation?.aadharCards?.[aadhaarIndex] || '',
        mediClaim: casePaperData?.mediClaim ? 'Yes' : 'No',
        insurance: casePaperData?.mediClaim || false,
        insuranceCompany: casePaperData?.insurance?.insuranceCompany || '',
        policyHolderName: casePaperData?.insurance?.policyHolderName || '',
        policyNumber: casePaperData?.insurance?.policyNumber || '',
        claimAmount: casePaperData?.insurance?.claimAmount || '',
        contactNumber: casePaperData?.insurance?.contactNumber || '',
        emergencyNumber: confirmation?.emergencyNumber || '',
        vehicleNo: confirmation?.vehicleNo || 'NO',
    };
};

// Helper function to build empty person object
const buildEmptyPerson = (personInfo, personType) => {
    return {
        id: personInfo?.id || '',
        name: personInfo?.name || '-',
        type: personType,
        aadhaarCardNumber: '',
        mediClaim: 'No',
        insurance: false,
        insuranceCompany: '',
        policyHolderName: '',
        policyNumber: '',
        claimAmount: '',
        contactNumber: '',
        emergencyNumber: '',
        vehicleNo: '',
    };
};

// Hook for initializing persons array with case paper data for all persons
export const usePersonsInitialValues = (orderDetail, patientData) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false);
    const [casePaperCache, setCasePaperCache] = useState({});
    const fetchedIdsRef = useRef(new Set());

    // Get all person IDs (main customer + guests)
    const allPersonIds = useMemo(() => {
        const ids = [];

        // Main customer ID
        const mainId = orderDetail?.customerId?._id || patientData?._id;
        if (mainId) {
            ids.push({
                id: mainId,
                type: 'main',
                name: orderDetail?.customerId?.name || patientData?.name
            });
        }

        // Guest IDs
        orderDetail?.additionalInfo?.guestDetails?.forEach((guest, index) => {
            const guestId = guest.id || guest._id;
            if (guestId) {
                ids.push({
                    id: guestId,
                    type: 'guest',
                    name: guest.name,
                    guestIndex: index
                });
            }
        });

        return ids;
    }, [orderDetail?.customerId?._id, orderDetail?.additionalInfo?.guestDetails, patientData?._id]);

    // Fetch case paper data for all persons
    useEffect(() => {
        const idsToFetch = allPersonIds.filter(
            person => person.id && !fetchedIdsRef.current.has(person.id)
        );

        if (idsToFetch.length === 0) return;

        setIsLoading(true);
        const totalFetches = idsToFetch.length;
        let completedFetches = 0;
        const newCacheData = { ...casePaperCache };

        idsToFetch.forEach(person => {
            fetchedIdsRef.current.add(person.id);

            dispatch(ActionGetCCPaperDetail(
                { Query: `/${person.id}` },
                (response) => {
                    if (response?.statusCode === 200 && response?.data) {
                        newCacheData[person.id] = response.data;
                    }

                    completedFetches++;
                    if (completedFetches === totalFetches) {
                        setCasePaperCache(newCacheData);
                        setIsLoading(false);
                    }
                }
            ));
        });
    }, [allPersonIds, dispatch]);

    // Reset cache when order changes
    useEffect(() => {
        fetchedIdsRef.current.clear();
        setCasePaperCache({});
    }, [orderDetail?._id]);

    // Extract confirmation data from main customer
    const confirmationData = useMemo(() => {
        const mainId = orderDetail?.customerId?._id || patientData?._id;
        const mainCasePaper = casePaperCache[mainId];
        if (!mainCasePaper?.confirmationDetails) return null;

        const confirmation = mainCasePaper.confirmationDetails;
        return {
            arrivingFrom: confirmation.arrivingFrom || '',
            arrivalTime: confirmation.arrivalTime || '',
        };
    }, [casePaperCache, orderDetail?.customerId?._id, patientData?._id]);

    // Build persons data with case paper info
    const personsData = useMemo(() => {
        const persons = [];
        const mainId = orderDetail?.customerId?._id || patientData?._id;
        const mainCasePaper = casePaperCache[mainId];

        // Build main customer
        const mainInfo = {
            id: mainId,
            name: orderDetail?.customerId?.name || patientData?.name || mainCasePaper?.name
        };

        if (mainCasePaper) {
            persons.push(buildPersonFromCasePaper(mainCasePaper, mainInfo, 'main', 0));
        } else {
            persons.push(buildEmptyPerson(mainInfo, 'main'));
        }

        // Build guests with their own case paper data
        orderDetail?.additionalInfo?.guestDetails?.forEach((guest, index) => {
            const guestId = guest.id || guest._id;
            const guestCasePaper = casePaperCache[guestId];
            const guestInfo = {
                id: guestId || `guest-${index}`,
                name: guest.name
            };

            if (guestCasePaper) {
                // Use guest's own case paper data
                persons.push(buildPersonFromCasePaper(guestCasePaper, guestInfo, 'guest', index));
            } else {
                // Fallback: try to get Aadhaar from main customer's confirmation
                const mainConfirmation = mainCasePaper?.confirmationDetails;

                if (mainConfirmation?.aadharCards?.[index + 1]) {
                    const guestWithData = {
                        ...buildEmptyPerson(guestInfo, 'guest'),
                        aadhaarCardNumber: mainConfirmation?.aadharCards?.[index + 1] || '',
                    };
                    persons.push(guestWithData);
                } else {
                    persons.push(buildEmptyPerson(guestInfo, 'guest'));
                }
            }
        });

        return persons;
    }, [
        casePaperCache,
        orderDetail?.customerId?._id,
        orderDetail?.customerId?.name,
        orderDetail?.additionalInfo?.guestDetails,
        patientData?._id,
        patientData?.name
    ]);

    return { personsData, confirmationData, isLoading };
};

// Hook for form submission
export const useFormSubmission = (bookingId, pageValue) => {
    const dispatch = useDispatch();
    const [isProcessing, setIsProcessing] = useState(false);
    const checkInCalledRef = useRef(false);

    const updatePerson = useCallback((person) => {
        return new Promise((resolve, reject) => {
            const updateData = {
                aadhaarCardNumber: person.aadhaarCardNumber,
                mediClaim: person.mediClaim === 'Yes',
                emergencyNumber: person.emergencyNumber,
                vehicleNo: person.vehicleNo,
                ...(person.mediClaim === 'Yes' && {
                    insurance: {
                        insuranceCompany: person.insuranceCompany,
                        policyHolderName: person.policyHolderName,
                        policyNumber: person.policyNumber,
                        claimAmount: person.claimAmount,
                        contactNumber: person.contactNumber,
                    },
                }),
            };

            dispatch(
                ActionUpdatePatient(
                    {
                        customerId: person.id,
                        obj: updateData,
                    },
                    (res) => {
                        if (res?.statusCode === 200) {
                            resolve({ success: true, name: person.name, type: person.type });
                        } else {
                            reject({
                                success: false,
                                name: person.name,
                                type: person.type,
                                error: res?.message || 'Update failed',
                            });
                        }
                    }
                )
            );
        });
    }, [dispatch]);

    const performCheckIn = useCallback(() => {
        return new Promise((resolve, reject) => {
            if (!bookingId) {
                reject(new Error('No booking ID provided'));
                return;
            }

            // Prevent duplicate calls
            if (checkInCalledRef.current) {
                resolve({ statusCode: 200, message: 'Already called' });
                return;
            }

            checkInCalledRef.current = true;
            dispatch(
                ActionCheckIn({ bookingId }, (res) => {
                    if (res?.statusCode === 200) {
                        resolve(res);
                    } else {
                        checkInCalledRef.current = false; // Reset on failure
                        reject(res);
                    }
                })
            );
        });
    }, [dispatch, bookingId]);

    const submitForm = useCallback(async (persons, arrivingFrom, arrivalTime) => {
        if (isProcessing) {
            return { success: false, error: 'Already processing' };
        }

        setIsProcessing(true);
        checkInCalledRef.current = false; // Reset at start

        try {
            // 1. Store Confirmation Detail
            const mainPerson = persons.find(p => p.type === 'main') || persons[0];
            const guests = persons.filter(p => p.type === 'guest');

            const confirmationData = {
                customerId: mainPerson.id,
                customerName: mainPerson.name,
                guestName: guests.map(g => g.name).join(', '),
                emergencyNumber: mainPerson.emergencyNumber,
                vehicleNo: mainPerson.vehicleNo,
                arrivingFrom,
                arrivalTime,
                aadharCards: persons.map(p => p.aadhaarCardNumber).filter(Boolean),
            };

            const storeConfirmationPromise = new Promise((resolve, reject) => {
                dispatch(CreatePatientConfirmationDetail(confirmationData, (res) => {
                    if (res?.statusCode === 200 || res?.success) {
                        resolve(res);
                    } else {
                        reject(res || new Error('Failed to store confirmation details'));
                    }
                }));
            });

            await storeConfirmationPromise;

            // 2. Update all persons profiles (Aadhaar, Mediclaim, etc.)
            const updatePromises = persons.map(person => updatePerson(person));
            const results = await Promise.allSettled(updatePromises);

            const successResults = results
                .filter(r => r.status === 'fulfilled')
                .map(r => r.value);

            const failedResults = results
                .filter(r => r.status === 'rejected')
                .map(r => r.reason);

            if (failedResults.length > 0) {
                setIsProcessing(false);
                return {
                    success: false,
                    error: 'Some updates failed',
                    failedResults,
                };
            }

            try {
                await performCheckIn();
                setIsProcessing(false);

                // Navigate back after successful check-in
                // setTimeout(() => {
                //     window.history.back();
                // }, 500);

                return {
                    success: true,
                    message: 'Check-in completed successfully!',
                };
            } catch (checkInError) {
                setIsProcessing(false);
                return {
                    success: false,
                    error: checkInError?.message || 'Check-in failed',
                };
            }

        } catch (error) {
            setIsProcessing(false);
            return {
                success: false,
                error: error.message || 'Unknown error occurred',
            };
        }
    }, [isProcessing, updatePerson, performCheckIn]);

    return { isProcessing, submitForm };
};
